/**
 * Shared Constants
 *
 * Centralized constants used across the monorepo.
 * Import from '@merchmuse/shared/constants' or '@merchmuse/shared'.
 */

import type { SubscriptionTier } from './types/user';

// ============================================================================
// Pagination
// ============================================================================

export const DEFAULT_PAGE_LIMIT = 20;
export const MAX_PAGE_LIMIT = 100;

// ============================================================================
// Input Limits
// ============================================================================

export const MAX_CUSTOM_INSTRUCTIONS_LENGTH = 1000;
export const MAX_SUPPORT_MESSAGE_LENGTH = 5000;
export const MIN_SUPPORT_MESSAGE_LENGTH = 10;
export const MAX_URL_LENGTH = 500;

// ============================================================================
// Subscription Tiers
// ============================================================================

export const TIER_CREDIT_ALLOCATIONS: Record<SubscriptionTier, number> = {
  free: 15,
  starter: 100,
  growth: 500,
  power: 2000,
} as const;

export const DEFAULT_CREDIT_ALLOCATION = TIER_CREDIT_ALLOCATIONS.free;

export const TIER_ORDER: Record<SubscriptionTier, number> = {
  free: 0,
  starter: 1,
  growth: 2,
  power: 3,
} as const;

// ============================================================================
// Credit Packs
// ============================================================================

export const CREDIT_PACKS = {
  basic: { credits: 40, price_cents: 999 },
  plus: { credits: 150, price_cents: 2999 },
  premium: { credits: 450, price_cents: 7999 },
} as const;

export const CENTS_PER_DOLLAR = 100;

// ============================================================================
// Time Durations
// ============================================================================

export const PRESIGNED_URL_EXPIRY_SECONDS = 3600; // 1 hour
export const PRESIGNED_URL_MAX_EXPIRY_SECONDS = 86400; // 24 hours
export const EXPORT_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
export const EXPORT_EXPIRY_SECONDS = 86400; // 24 hours
export const CACHE_MAX_AGE_ONE_YEAR = 31536000; // 365 days
export const WEBHOOK_TIMESTAMP_TOLERANCE_SECONDS = 300; // 5 minutes
export const CORS_MAX_AGE_SECONDS = 86400; // 24 hours
export const MIN_CACHE_TTL_SECONDS = 60; // 1 minute

// ============================================================================
// Refund Policy
// ============================================================================

export const REFUND_WINDOW_DAYS = 7;

// Reference ID prefixes used across credit_transactions and refund_policy_events
export const POLAR_ORDER_PREFIX = 'polar_order:';
export const POLAR_REFUND_PREFIX = 'polar_refund:';

// ============================================================================
// AI Configuration
// ============================================================================

export const DEFAULT_AI_CONFIDENCE = 0.8;
export const FALLBACK_AI_CONFIDENCE = 0.7;
export const LOW_AI_CONFIDENCE = 0.5;

// ============================================================================
// Processing
// ============================================================================

export const BASE64_CHUNK_SIZE = 8192;
export const FAST_FILTER_PIXEL_THRESHOLD = 4_000_000;
export const IMAGE_HEADER_BYTES = 65536; // 64KB for header parsing
export const ERROR_LOG_MAX_LENGTH = 200;
export const RETRY_BASE_DELAY_MS = 10;
